import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import API from '../utils/apiconfig';
import { useAuth } from '../context/Authcontext';
import { useGlobalError } from '../context/GlobalErrorContext';
import TablePagination from '../components/TablePagination';
import { ScrollableListBody } from '../components/ScrollableTable';
import { formatEmiWindowDisplay } from '../utils/planStatus';
import { DEFAULT_PAGE_SIZE, parsePaginatedResponse } from '../utils/pagination';
import { formatAppDate } from '../utils/dateFormat';

function Membership() {
  const { user } = useAuth();
  const { reportError } = useGlobalError();
  const navigate = useNavigate();
  const [membership, setMembership] = useState(null);
  const [payments, setPayments] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [paymentsLoading, setPaymentsLoading] = useState(true);

  const loadPayments = useCallback(async () => {
    setPaymentsLoading(true);
    try {
      const p = await api.get(API.PAYMENT_HISTORY, { params: { page, page_size: pageSize } });
      const parsed = parsePaginatedResponse(p.data, ['results']);
      setPayments(parsed.items);
      setTotalPages(parsed.totalPages);
      setTotalCount(parsed.count);
      setPage(parsed.page);
    } catch (err) {
      reportError(err);
      setPayments([]);
      setTotalPages(1);
      setTotalCount(0);
    } finally {
      setPaymentsLoading(false);
    }
  }, [page, pageSize, reportError]);

  useEffect(() => {
    if (!user?.id) {
      navigate('/login');
      return;
    }
    api.get(API.MEMBERSHIP)
      .then((m) => {
        setMembership(m.data);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        reportError(err);
      });
  }, [user?.id, navigate, reportError]);

  useEffect(() => {
    if (!user?.id) return;
    loadPayments();
  }, [user?.id, loadPayments]);

  const isActive = membership?.is_active;
  const statusLabel = user?.membership_status || membership?.status || 'none';

  if (loading) {
    return <div className="min-h-screen bg-slate-50" aria-hidden />;
  }

  return (
    <div className="min-h-screen bg-slate-50 py-10">
      <div className="max-w-2xl mx-auto px-6 space-y-5">
        <div className="border-b border-slate-200 pb-5">
          <h1 className="text-2xl font-black text-slate-900" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>Membership</h1>
          <p className="text-sm text-slate-400 mt-1">Status and legacy payment history</p>
        </div>

        <div className={`rounded-2xl p-6 border ${isActive ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-100 border-slate-200'}`}>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <p className={`text-xs font-bold uppercase tracking-widest mb-1 ${isActive ? 'text-emerald-600' : 'text-slate-500'}`}>
                Membership Status
              </p>
              <p className={`text-3xl font-black capitalize ${isActive ? 'text-emerald-700' : 'text-slate-600'}`}
                style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>
                {isActive ? 'Active' : statusLabel === 'none' ? 'Not joined' : statusLabel}
              </p>
              {isActive && membership?.expires_at && (
                <p className="text-emerald-600 text-sm mt-1">
                  Renews {formatAppDate(membership.expires_at)}
                  · {formatEmiWindowDisplay(membership.days_remaining).value === 'Open'
                    ? 'Payment window open'
                    : `${membership.days_remaining} days until window opens`}
                </p>
              )}
            </div>
          </div>
        </div>

        {!isActive && (
          <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
            <h2 className="text-lg font-black text-slate-900" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>
              Activate via plan payment
            </h2>
            <p className="text-sm text-slate-500">
              Membership activates after you choose a plan, upload payment proof, and admin approves it.
              Direct ₹500 activation is no longer available.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/plans"
                className="inline-block bg-slate-900 hover:bg-slate-700 text-white text-sm font-bold px-5 py-3 rounded-xl no-underline transition-colors"
              >
                Choose a plan
              </Link>
              <Link
                to="/payments"
                className="inline-block bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-semibold px-5 py-3 rounded-xl no-underline transition-colors"
              >
                View payments
              </Link>
            </div>
          </div>
        )}

        {isActive && (
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <p className="text-sm font-bold text-slate-800 mb-1">Need another plan?</p>
            <p className="text-xs text-slate-500 mb-4">Manage EMI or full settlement from the Payments page.</p>
            <Link
              to="/payments"
              className="inline-block bg-slate-900 hover:bg-slate-700 text-white text-sm font-bold px-5 py-2.5 rounded-xl no-underline transition-colors"
            >
              Go to Payments
            </Link>
          </div>
        )}

        {(paymentsLoading || payments.length > 0) && (
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Legacy payment history</p>
            </div>
            {paymentsLoading && payments.length === 0 ? null : (
              <>
                <ScrollableListBody ariaLabel="Legacy payment history">
                  {payments.map((p) => {
                    const paidOn = formatAppDate(p.created_at);
                    return (
                      <div key={p.id} className="flex items-center gap-4 px-5 py-4">
                        <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600 text-xs font-black flex-shrink-0">✓</div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-800">
                            {p.is_renewal ? 'Membership Renewal' : 'New Membership'}
                          </p>
                          <p className="text-xs text-slate-400">{paidOn}</p>
                        </div>
                        <span className="text-sm font-black text-slate-900">
                          ₹{parseFloat(p.amount).toLocaleString('en-IN')}
                        </span>
                      </div>
                    );
                  })}
                </ScrollableListBody>
                <TablePagination
                  page={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                  pageSize={pageSize}
                  onPageSizeChange={(next) => { setPageSize(next); setPage(1); }}
                  disabled={paymentsLoading}
                  count={totalCount}
                  embedded
                  className="px-4"
                />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Membership;
